import type { LanguageCode, TranslationNamespace } from '@/types/i18n';
import type { LoadTranslationsResult, LocaleDegradedFallback } from './translations';

export type LocaleActivationSource = LoadTranslationsResult['source'];

export interface LocaleActivation {
  requestedLanguage: LanguageCode;
  actualLanguage: LanguageCode;
  translations: TranslationNamespace;
  version: string;
  degradedFallback: LocaleDegradedFallback;
  source: LocaleActivationSource;
  routeId: string | 'all';
}

export interface LocaleLifecycleState {
  preferredLanguage: LanguageCode;
  activeLanguage: LanguageCode | null;
  pendingLanguage: LanguageCode | null;
  translations: TranslationNamespace | null;
  activeRouteId: string | 'all' | null;
  loadedLanguages: LanguageCode[];
  version: string | null;
  cacheSource: LocaleActivationSource | 'bootstrap_bundle' | null;
  observedVersion: string | null;
  isLoading: boolean;
  isInitialLocaleReady: boolean;
  degradedFallback: LocaleDegradedFallback;
  error: string | null;
  generation: number;
  lastEvent:
    | 'initialize'
    | 'select'
    | 'cache_hit'
    | 'fetch_success'
    | 'fetch_failure'
    | 'retry'
    | 'version_change'
    | 'bootstrap_ready'
    | 'optional_ready';
}

export type LocaleLifecycleEvent =
  | { type: 'select'; language: LanguageCode; generation: number }
  | { type: 'retry'; generation: number }
  | { type: 'version_change'; generation: number; version: string }
  | { type: 'cache_hit'; generation: number; activation: LocaleActivation }
  | { type: 'fetch_success'; generation: number; activation: LocaleActivation }
  | { type: 'fetch_failure'; generation: number; error: string }
  | {
    type: 'bootstrap_ready';
    generation: number;
    language: LanguageCode;
    translations: TranslationNamespace;
  }
  | {
    type: 'optional_ready';
    language: LanguageCode;
    version: string;
    translations: TranslationNamespace;
  };

export interface InitialLocaleLifecycleInput {
  preferredLanguage: LanguageCode;
  translations: TranslationNamespace | null;
  version: string | null;
  isVerifiedPayload: boolean;
  degradedFallback: LocaleDegradedFallback;
  cacheSource: LocaleActivationSource | 'bootstrap_bundle' | null;
}

function pendingLanguage(
  preferredLanguage: LanguageCode,
  activeLanguage: LanguageCode | null,
): LanguageCode | null {
  return preferredLanguage === activeLanguage ? null : preferredLanguage;
}

function beginRequest(
  state: LocaleLifecycleState,
  event: Extract<LocaleLifecycleEvent, { type: 'select' | 'retry' | 'version_change' }>,
): LocaleLifecycleState {
  const preferredLanguage = event.type === 'select'
    ? event.language
    : state.preferredLanguage;

  return {
    ...state,
    preferredLanguage,
    pendingLanguage: pendingLanguage(preferredLanguage, state.activeLanguage),
    observedVersion: event.type === 'version_change' ? event.version : state.observedVersion,
    isLoading: true,
    error: null,
    generation: event.generation,
    lastEvent: event.type,
  };
}

function activate(
  state: LocaleLifecycleState,
  event: Extract<LocaleLifecycleEvent, { type: 'cache_hit' | 'fetch_success' }>,
): LocaleLifecycleState {
  if (event.generation !== state.generation) return state;

  const { activation } = event;
  if (activation.requestedLanguage !== state.preferredLanguage) return state;
  const loadedLanguages = state.loadedLanguages.includes(activation.actualLanguage)
    ? state.loadedLanguages
    : [...state.loadedLanguages, activation.actualLanguage];

  return {
    ...state,
    activeLanguage: activation.actualLanguage,
    pendingLanguage: pendingLanguage(
      state.preferredLanguage,
      activation.actualLanguage,
    ),
    translations: activation.translations,
    activeRouteId: activation.routeId,
    loadedLanguages,
    version: activation.version,
    cacheSource: activation.source,
    observedVersion: activation.version,
    isLoading: false,
    isInitialLocaleReady: true,
    degradedFallback: activation.degradedFallback,
    error: null,
    lastEvent: event.type,
  };
}

export function createInitialLocaleLifecycleState(
  input: InitialLocaleLifecycleInput,
): LocaleLifecycleState {
  const activeLanguage = input.translations ? input.preferredLanguage : null;
  return {
    preferredLanguage: input.preferredLanguage,
    activeLanguage,
    pendingLanguage: pendingLanguage(input.preferredLanguage, activeLanguage),
    translations: input.translations,
    activeRouteId: input.translations ? 'all' : null,
    loadedLanguages: activeLanguage ? [activeLanguage] : [],
    version: input.version,
    cacheSource: input.cacheSource,
    observedVersion: input.version,
    isLoading: false,
    isInitialLocaleReady: input.isVerifiedPayload,
    degradedFallback: input.degradedFallback,
    error: null,
    generation: 0,
    lastEvent: 'initialize',
  };
}

export function localeLifecycleReducer(
  state: LocaleLifecycleState,
  event: LocaleLifecycleEvent,
): LocaleLifecycleState {
  switch (event.type) {
    case 'select':
    case 'retry':
    case 'version_change':
      return beginRequest(state, event);

    case 'cache_hit':
    case 'fetch_success':
      return activate(state, event);

    case 'fetch_failure':
      if (event.generation !== state.generation) return state;
      return {
        ...state,
        pendingLanguage: pendingLanguage(
          state.preferredLanguage,
          state.activeLanguage,
        ),
        isLoading: false,
        degradedFallback: state.translations ? 'previous_locale' : null,
        error: event.error,
        lastEvent: event.type,
      };

    case 'bootstrap_ready':
      if (event.generation !== state.generation) return state;
      return {
        ...state,
        activeLanguage: event.language,
        pendingLanguage: pendingLanguage(state.preferredLanguage, event.language),
        translations: event.translations,
        activeRouteId: 'all',
        loadedLanguages: state.loadedLanguages.includes(event.language)
          ? state.loadedLanguages
          : [...state.loadedLanguages, event.language],
        isLoading: false,
        isInitialLocaleReady: true,
        degradedFallback: 'bootstrap_bundle',
        cacheSource: 'bootstrap_bundle',
        lastEvent: event.type,
      };

    case 'optional_ready':
      if (
        state.activeLanguage !== event.language
        || state.version !== event.version
      ) {
        return state;
      }
      return {
        ...state,
        translations: event.translations,
        lastEvent: event.type,
      };
  }
}
